/**
 * Turn countdown display for flip timer
 *
 * Freezes while the game is paused or during the resume countdown; the server
 * sends a shifted turnStartedAt in GAME_RESUMED so the countdown continues
 * from where it stopped.
 */

import { useState, useEffect } from 'react';
import clsx from 'clsx';
import { useGameStore, selectIsMyTurn } from '../../stores/gameStore';

export function TurnCountdown() {
  const turnTimerMs = useGameStore((s) => s.gameState?.turnTimerMs);
  const turnStartedAt = useGameStore((s) => s.gameState?.turnStartedAt);
  const isMyTurn = useGameStore(selectIsMyTurn);
  const isFrozen = useGameStore((s) => (s.gameState?.isPaused ?? false) || s.resumeCountdownEndsAt !== null);
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);

  useEffect(() => {
    // Timer disabled or no turn started
    if (!turnTimerMs || !turnStartedAt) {
      setSecondsLeft(null);
      return;
    }

    const endsAt = turnStartedAt + turnTimerMs;

    // Paused / resuming: keep showing the last value
    if (isFrozen) {
      setSecondsLeft((prev) => prev ?? Math.max(0, Math.ceil((endsAt - Date.now()) / 1000)));
      return;
    }

    const update = () => {
      const remaining = Math.max(0, Math.ceil((endsAt - Date.now()) / 1000));
      setSecondsLeft(remaining);
    };

    update();
    const interval = setInterval(update, 100);
    return () => clearInterval(interval);
  }, [turnTimerMs, turnStartedAt, isFrozen]);

  // Don't render if timer is disabled or no countdown
  if (secondsLeft === null) {
    return null;
  }

  const isOverdue = secondsLeft === 0;
  const isUrgent = secondsLeft <= 2 && secondsLeft > 0;

  return (
    <div
      className={clsx(
        'flex items-center gap-1 text-sm font-mono tabular-nums shrink-0',
        isFrozen && 'opacity-60',
        !isFrozen && isMyTurn && isOverdue && 'text-red-600 animate-pulse font-bold',
        !isFrozen && isMyTurn && isUrgent && 'text-red-500 animate-pulse font-bold',
        isMyTurn && (isFrozen || (!isUrgent && !isOverdue)) && 'text-blue-600',
        !isMyTurn && 'text-slate-400'
      )}
      title={isFrozen ? 'Paused' : isMyTurn ? (isOverdue ? 'Time expired - flip now!' : 'Time left to flip a tile') : "Opponent's turn timer"}
      role="timer"
      aria-live={!isFrozen && isMyTurn && (isUrgent || isOverdue) ? 'assertive' : 'off'}
    >
      <span>{isOverdue ? 'FLIP!' : `${secondsLeft}s`}</span>
    </div>
  );
}
